import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, CreditCard, Settings, HelpCircle, LogOut, ChevronRight, Edit2, BarChart3, Clock, Mic, Share2, Bell, Sparkles, Database, Wrench } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { SubscriptionPlan } from '../data/subscriptionPlans';
interface ProfileScreenProps {
  onNavigate: (screen: string) => void;
  onLogout: () => void;
  isActivated?: boolean;
  plan?: SubscriptionPlan;
}
export function ProfileScreen({
  onNavigate,
  onLogout,
  isActivated: activatedProp = false,
  plan
}: ProfileScreenProps) {
  const {
    isLight
  } = useTheme();
  const [isActivated, setIsActivated] = useState(activatedProp);
  const [hasData, setHasData] = useState(false);
  const [showDevTools, setShowDevTools] = useState(false);
  useEffect(() => {
    setIsActivated(activatedProp);
  }, [activatedProp]);
  const menuItems = [{
    icon: Mic,
    label: 'Caller Experience',
    screen: 'callerExperience'
  }, {
    icon: BarChart3,
    label: 'Usage',
    screen: 'usage'
  }, {
    icon: Bell,
    label: 'Notifications',
    screen: 'notifications'
  }, {
    icon: Settings,
    label: 'Settings',
    screen: 'settings'
  }, {
    icon: Share2,
    label: 'Share App',
    action: () => {
      if (navigator.share) {
        navigator.share({
          title: 'Vexu.ai',
          text: 'Check out Vexu.ai — your AI answering machine!',
          url: 'https://vexu.ai'
        });
      }
    }
  }, {
    icon: HelpCircle,
    label: 'Help & Support',
    screen: 'support'
  }];
  return <div className="h-full flex flex-col p-6 pb-36 overflow-y-auto relative" data-id="element-3661" data-mp="nmm3115gmcp6">
      {/* Background orb */}
      <div className={`absolute top-10 right-0 w-40 h-40 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-3662" data-mp="3w25rznz3l8yf" />

      {/* Header */}
      <div className="flex justify-between items-center mb-6 relative z-10" data-id="element-3663" data-mp="1v0zi9pmdyxdz">
        <h1 className={`text-2xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3664" data-mp="2002ag9h9p94j">
          Account
        </h1>
        <button onClick={() => setShowDevTools(!showDevTools)} className={`p-2 rounded-full transition-colors ${showDevTools ? isLight ? 'bg-slate-200 text-slate-700' : 'bg-white/15 text-white' : isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-3665" data-mp="24sdkw69mwdbc">
          <Wrench className="w-4 h-4" data-id="element-3666" data-mp="1h3uczruhy2js" />
        </button>
      </div>

      {/* Designer Controls (collapsible) */}
      <AnimatePresence data-id="element-3667" data-mp="1q2fudjo8fbiz">
        {showDevTools && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden mb-4 relative z-10" data-id="element-3668" data-mp="1q491t16o5zs1">
            <div className="flex items-center gap-2 pb-2" data-id="element-3669" data-mp="31vymnom3vrbr">
              <button onClick={() => setIsActivated(!isActivated)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${isActivated ? isLight ? 'bg-vexu/10 border-vexu/20 text-vexu-700' : 'bg-vexu/20 border-vexu/30 text-vexu' : isLight ? 'bg-slate-100 border-slate-200 text-slate-600' : 'bg-white/10 border-white/10 text-zinc-300'}`} data-id="element-3670" data-mp="31uf57uopra8e">
                <Sparkles className="w-3 h-3" data-id="element-3671" data-mp="2evio0aboxcbw" />
                {isActivated ? 'Activated' : 'Not Activated'}
              </button>
              <button onClick={() => setHasData(!hasData)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${hasData ? isLight ? 'bg-slate-800 border-slate-900 text-white' : 'bg-white border-white text-black' : isLight ? 'bg-white border-slate-200 text-slate-700 shadow-sm' : 'bg-dark-card border-white/10 text-zinc-300'}`} data-id="element-3672" data-mp="h60who4xh89z">
                <Database className="w-3 h-3" data-id="element-3673" data-mp="b6e8emrzexke" />
                {hasData ? 'Clear Data' : 'Simulate Data'}
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="space-y-5 relative z-10" data-id="element-3674" data-mp="1fjm5ieqjxyy">
        {/* Avatar Card */}
        <div className={`flex flex-col items-center backdrop-blur-xl border rounded-2xl p-5 shadow-lg transition-colors duration-500 ${isLight ? 'bg-white/80 border-slate-200/60 shadow-slate-200/50' : 'bg-dark-card/60 border-white/10 shadow-black/50'}`} data-id="element-3675" data-mp="68cqw6a7f6b1">
          <div className="relative mb-3" data-id="element-3676" data-mp="3emuu9up79xob">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold border transition-colors ${isLight ? 'bg-slate-100 text-slate-400 border-slate-200' : 'bg-white/5 text-zinc-400 border-white/10'}`} data-id="element-3677" data-mp="3eo65gfkz67pg">
              AJ
            </div>
            <button className={`absolute bottom-0 right-0 p-1.5 rounded-full shadow-lg border-2 transition-colors ${isLight ? 'bg-vexu text-white shadow-vexu/20 border-white' : 'bg-vexu text-black shadow-vexu/20 border-dark'}`} data-id="element-3678" data-mp="2v3fep5j58owi">
              <Edit2 className="w-3 h-3" data-id="element-3679" data-mp="3iyrvh6mjz9lb" />
            </button>
          </div>

          <h2 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3680" data-mp="3e0qcelp4na9u">
            Alex Johnson
          </h2>

          <div className="flex items-center gap-2 mt-1 mb-3" data-id="element-3681" data-mp="3sotgtqniudwj">
            <Phone className={`w-3.5 h-3.5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-3682" data-mp="3nq0440g26qxf" />
            <span className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3683" data-mp="5lf2nxf33vey">
              +1 (555) 123-4567
            </span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-3684" data-mp="2ue231oanssqs">
              Primary
            </span>
          </div>

          {isActivated ? <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-3685" data-mp="384os0oqmb49o">
              <div className={`w-2 h-2 rounded-full ${isLight ? 'bg-vexu shadow-[0_0_4px_rgba(0,200,83,0.5)]' : 'bg-vexu shadow-[0_0_8px_rgba(0,200,83,0.8)]'}`} data-id="element-3686" data-mp="3d3i4cdu64p89" />
              <span className={`text-sm font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-3687" data-mp="3n3ip847ordek">
                Active
              </span>
            </div> : <button onClick={() => onNavigate('planSelection')} className="px-5 py-2 rounded-full text-sm font-bold bg-vexu text-white hover:bg-vexu-600 transition-colors" data-id="element-3688" data-mp="1sprd8y1vwhfy">
              Activate Vexu
            </button>}
        </div>

        {/* Compact Plan & Usage Card */}
        {isActivated && hasData && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`backdrop-blur-xl border rounded-2xl p-4 shadow-sm transition-colors ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-3689" data-mp="1ffnzxw6cqehs">
            <div className="flex items-center justify-between mb-3" data-id="element-3690" data-mp="1z46p4drcxrwh">
              <div className="flex items-center gap-2.5" data-id="element-3691" data-mp="1z69lrgn0mja6">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isLight ? 'bg-purple-50 text-purple-600' : 'bg-purple-500/10 text-purple-400'}`} data-id="element-3692" data-mp="1u4k8ducbltix">
                  <CreditCard className="w-4 h-4" data-id="element-3693" data-mp="ipdskun9ot4t" />
                </div>
                <div data-id="element-3694" data-mp="2is6k3xvxxi19">
                  <span className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3695" data-mp="2irn2kia7cvnr">
                    Vexu {plan?.name ?? 'Starter'}
                  </span>
                  <span className={`text-[10px] font-medium ml-2 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-3696" data-mp="28w37tz7dk30l">
                    Active
                  </span>
                </div>
              </div>
              <button onClick={() => onNavigate('planSelection')} className={`text-xs font-medium ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-white'}`} data-id="element-3697" data-mp="36hr32gs3gfaj">
                Manage
              </button>
            </div>
            <div className="flex items-center justify-between mb-1.5" data-id="element-3698" data-mp="3ki1wnrksg7ze">
              <div className="flex items-center gap-1.5" data-id="element-3699" data-mp="3kfp5cnt54l9o">
                <Clock className={`w-3 h-3 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-3700" data-mp="3p9jva8zc9wh7" />
                <span className={`text-[11px] font-medium ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3701" data-mp="1drouv5bygcu2">
                  Minutes Used
                </span>
              </div>
              <span className={`text-[11px] font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-3702" data-mp="1mnnyugw4cc34">
                {Math.round((plan?.minutes ?? 180) * 0.37)} /{' '}
                {plan?.minutes ?? 180} min
              </span>
            </div>
            <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-slate-100' : 'bg-white/10'}`} data-id="element-3703" data-mp="pxngazmceru9">
              <div className="h-full rounded-full bg-vexu transition-all duration-500" style={{
            width: '37%'
          }} data-id="element-3704" data-mp="yvr27x18pqml" />
            </div>
          </motion.div>}

        {/* Menu List */}
        <div className={`backdrop-blur-xl border rounded-2xl overflow-hidden shadow-sm transition-colors duration-500 ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-3705" data-mp="2ads22n5ea9tl">
          {menuItems.map((item, i) => {
          const Icon = item.icon;
          return <button key={i} onClick={() => 'action' in item && item.action ? item.action() : onNavigate(item.screen!)} className={`w-full p-4 flex items-center justify-between transition-colors ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5'} ${i < menuItems.length - 1 ? isLight ? 'border-b border-slate-100' : 'border-b border-white/5' : ''}`} data-id="element-3706" data-mp="1ole2hj3x3zwa" data-mpi={i}>
                <div className="flex items-center gap-3" data-id="element-3707" data-mp="341mib1uppgdf">
                  <Icon className={`w-5 h-5 ${isLight ? 'text-slate-400' : 'text-zinc-400'}`} data-id="element-3708" data-mp="3413bohaiieep" />
                  <span className={`font-medium text-sm ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-3709" data-mp="lsrzjwbwwhph">
                    {item.label}
                  </span>
                </div>
                <ChevronRight className={`w-4 h-4 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-3710" data-mp="vi36bcbbtaa7" />
              </button>;
        })}
        </div>

        {/* Sign Out */}
        <motion.button onClick={onLogout} whileTap={{
        scale: 0.98
      }} className={`group w-full p-4 flex items-center gap-3 rounded-2xl transition-all duration-200 border ${isLight ? 'bg-white border-red-200/70 hover:border-red-300 hover:bg-red-50 shadow-sm' : 'bg-red-500/[0.07] border-red-500/20 hover:bg-red-500/[0.12] hover:border-red-500/40'}`} data-id="element-3711" data-mp="2uz57ifctg3p">
          <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isLight ? 'bg-red-100 text-red-600 group-hover:bg-red-500 group-hover:text-white' : 'bg-red-500/15 text-red-400 group-hover:bg-red-500 group-hover:text-white'}`} data-id="element-3712" data-mp="1n999y2q99q48">
            <LogOut className="w-[18px] h-[18px]" data-id="element-3713" data-mp="3k9vmwymb3032" />
          </span>
          <span className="flex flex-col items-start leading-tight" data-id="element-3714" data-mp="23h9d0ry8s57">
            <span className={`font-bold text-sm ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-3715" data-mp="3tb38cg3q46fq">
              Sign Out
            </span>
            <span className={`text-[11px] font-medium ${isLight ? 'text-red-400' : 'text-red-500/70'}`} data-id="element-3716" data-mp="35lirc2r4rj93">
              You'll need to sign in again
            </span>
          </span>
          <ChevronRight className={`w-4 h-4 ml-auto flex-shrink-0 transition-transform group-hover:translate-x-0.5 ${isLight ? 'text-red-300' : 'text-red-500/50'}`} data-id="element-3717" data-mp="3fcvg2lhmttaw" />
        </motion.button>
      </div>
    </div>;
}